import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSettings } from "@/lib/settings";
import { BACKEND_URL } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { trackLead } from "@/lib/analytics";
import { ChevronDown, ShieldCheck, Lock, BadgeCheck, Sparkles } from "@/lib/icons";
/* ---------- Shared bits ---------- */

export function PageShell({ children }) {
  return (
    <div className="relative container-tight py-16 md:py-20">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[500px] h-[500px] -top-40 -left-20 opacity-35" />
        <div className="blob blob-magenta w-[420px] h-[420px] top-40 right-0 opacity-25" style={{ animationDelay: "-8s" }} />
      </div>
      <div className="relative z-10 fade-up">{children}</div>
    </div>
  );
}

export function LongCopy({ paragraphs }) {
  return (
    <div className="space-y-5 text-white/70 text-[15px] md:text-base leading-relaxed">
      {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
    </div>
  );
}

/* ---------- Content: About PRONET ---------- */

const ABOUT_PARAGRAPHS = [
  "PRONET, short for Photographers Network, is a professional platform built to connect photographers, videographers, editors, drone operators, and other wedding and event professionals across India.",
  "Our mission is simple: build one trusted community where professionals can showcase their work, grow their network, find genuine business opportunities, and collaborate with fellow creators.",
  "The idea for PRONET came from problems every professional in this industry knows well. Finding a reliable local photographer on short notice. Arranging a second shooter for a big wedding. Hiring the right crew for an outstation shoot. Handling a last-minute emergency when someone drops out. Getting an event team together quickly without relying on scattered WhatsApp groups and word of mouth. PRONET exists to solve these problems in one place.",
  "Whether you run a solo photography practice, own a full-fledged studio, edit for a living, or specialize in any part of wedding and event coverage, PRONET is built to make you more visible and more accessible to the clients and industry partners who need you.",
  "We are not building just another directory. We are building India's most trusted professional network for the photography and videography community, one where quality of work and reliability actually count for something.",
  "Pro ID profiles, digital ID cards, professional portfolios, and secure networking tools sit at the core of how PRONET works. Together, they help members build credibility, connect with confidence, and access opportunities that were previously hard to find.",
  "This is a community built by professionals, for professionals. Together, we are building a stronger photography community across India.",
];

export function About() {
  const { settings } = useSettings();
  const a = settings.about_page || {};
  const aboutParagraphs = a.body_paragraphs?.length ? a.body_paragraphs : ABOUT_PARAGRAPHS;
  const [open, setOpen] = useState(false);

  useSeo({
    title: "About PRONET | India's Professional Photography Network",
    description: "PRONET connects photographers, videographers, editors and event professionals across India through reviewed profiles and Pro IDs.",
  });

  return (
    <PageShell>
      <div className="overline text-fuchsia-300" data-testid="about-overline">{a.overline || "About PRONET"}</div>
      <h1 className="mt-3 font-display text-5xl md:text-7xl leading-none text-white" data-testid="about-title">
        {a.title_prefix || "India's"} <span className="italic text-gradient">{a.title_accent || "professional"}</span>
        {a.title_suffix ? <><br />{a.title_suffix}</> : null}
      </h1>

      {/* Expandable About card — click to reveal the full story */}
      <div className="mt-10 max-w-3xl">
        <button
          type="button"
          onClick={() => setOpen((x) => !x)}
          data-testid="about-card-toggle"
          aria-expanded={open}
          className="w-full text-left glass-strong rounded-3xl p-6 md:p-8 group hover:-translate-y-0.5 transition-transform duration-500"
        >
          <div className="flex items-center gap-4">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/30 to-magenta-500/30 border border-fuchsia-400/25 text-fuchsia-300 shrink-0">
              <Sparkles size={22} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="overline text-fuchsia-300 text-[10px]">Our story</div>
              <div className="mt-1 font-display text-2xl md:text-3xl text-white leading-tight">
                About PRONET
              </div>
              <div className="mt-1 text-sm text-white/50">
                {open ? "Click to collapse." : "Click to read our full mission and vision."}
              </div>
            </div>
            <ChevronDown
              size={20}
              className={`text-fuchsia-300 transition-transform duration-500 ${open ? "rotate-180" : ""}`}
            />
          </div>
          {open && (
            <div className="mt-8 pt-8 border-t border-white/10" data-testid="about-card-content">
              <LongCopy paragraphs={aboutParagraphs} />
            </div>
          )}
        </button>
      </div>

      {/* Pillars — optional CMS content, unchanged */}
      {(a.pillars || []).length > 0 && (
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {(a.pillars || []).map((f, i) => (
            <div key={i} className="glass rounded-3xl p-8 card-hover" data-testid={`about-pillar-${i}`}>
              <div className="font-display text-3xl text-white">{f.title}</div>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Cross-links */}
      <div className="mt-16 flex flex-wrap items-center gap-3 text-xs text-white/50">
        <Link to="/verification" className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 font-semibold text-white">
          <BadgeCheck size={13} className="text-emerald-300" /> How verification works
        </Link>
        <Link to="/privacy" className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 font-semibold text-white">
          <Lock size={13} className="text-fuchsia-300" /> Privacy Policy
        </Link>
      </div>
    </PageShell>
  );
}

/* ---------- Content: Privacy Policy ---------- */

const PRIVACY_PARAGRAPHS = [
  "At PRONET, protecting the privacy and personal information of our members is one of our highest priorities.",
  "We collect only the information required to create and manage member profiles and to deliver our professional networking services. This includes details such as phone numbers, email addresses, identification documents, and other data needed for verification and communication.",
  "This information is stored securely and is never shared publicly without the member's permission or outside the boundaries of their chosen privacy settings. Members have full control over who can see their profile information through the privacy settings in their account.",
  "We use reasonable and appropriate security measures to protect member data from unauthorized access, misuse, or disclosure. We do not sell, rent, or share members' personal information with third parties for marketing purposes.",
  "Documents uploaded for verification purposes are accessible only to authorized administrators responsible for reviewing and confirming member identities. No one else within or outside PRONET has access to this information.",
  "Members are responsible for keeping their login credentials secure and for making sure the information they provide is accurate and kept up to date.",
  "By using PRONET, you agree to this Privacy Policy along with our Terms of Service.",
  "This Privacy Policy may be updated periodically to improve our services and to remain in compliance with applicable laws. Members are encouraged to review this page from time to time to stay informed of any changes.",
];

export function Privacy() {
  const { settings } = useSettings();
  const p = settings.privacy_page || {};
  const paragraphs = p.paragraphs?.length ? p.paragraphs : PRIVACY_PARAGRAPHS;
  useSeo({
    title: "Privacy Policy | PRONET",
    description: "How PRONET collects, uses and protects member information across the photographers network.",
  });
  return (
    <PageShell>
      <div className="overline text-fuchsia-300">Legal</div>
      <h1 className="mt-3 font-display text-5xl md:text-6xl leading-none text-white" data-testid="privacy-title">
        Privacy <span className="italic text-gradient">Policy</span>
      </h1>
      <p className="mt-4 text-white/50 text-sm">Last updated: {p.last_updated || "February 2026"}</p>

      <div className="mt-10 glass-strong rounded-3xl p-6 md:p-10 max-w-3xl">
        <LongCopy paragraphs={paragraphs} />
      </div>

      <div className="mt-10 flex items-center gap-3 text-xs">
        <Link to="/about" className="text-fuchsia-300 hover:underline">Back to About</Link>
        <span className="text-white/30">·</span>
        <Link to="/verification" className="text-fuchsia-300 hover:underline">Verification Process</Link>
      </div>
    </PageShell>
  );
}

/* ---------- Content: Verification Process ---------- */

const VERIFICATION_PARAGRAPHS = [
  "At PRONET (Photographers Network), verification is the foundation of our community. We believe that a professional network is built on trust, and trust begins with knowing the people you connect, collaborate, and work with.",
  "Every member who registers on PRONET is reviewed before receiving Pro ID status. This helps maintain the quality and credibility of the community.",
  "During the verification process, our team carefully reviews the information and documents submitted by the applicant. Depending on the member category and available information, this may include reviewing identity details, professional information, portfolio, studio details, contact information, and other supporting documents provided during registration. Additional information may be requested whenever necessary to complete the review.",
  "Our goal is not to approve every application. Our goal is to build a community of genuine professionals who are committed to their work, respect their clients and fellow members, and contribute positively to the photography and videography industry. We believe that a trusted network is always more valuable than a large network filled with unverified or unreliable profiles.",
  "Once approved, members receive a PRONET Pro ID and a Digital Membership ID Card with a unique membership number and QR code. The QR code allows others to verify the authenticity of the member's profile directly through PRONET. This provides greater confidence when collaborating, hiring, or referring work.",
  "Verification confirms that the member's application has been reviewed based on the information available at the time of approval. However, verification should not be interpreted as a guarantee of future conduct, performance, or business practices. Professional responsibility always remains with the individual member.",
  "To maintain the integrity of the community, every Pro ID member is expected to follow PRONET's Community Guidelines and Code of Professional Conduct. Members who provide false information, misuse the platform, engage in unethical practices, repeatedly fail to honor professional commitments, or violate community standards may have their Pro ID status suspended or removed. In serious cases, PRONET reserves the right to permanently terminate membership without prior notice.",
  "Our verification process is designed to create a safer and more reliable environment for photographers, videographers, studios, clients, and creative professionals across India. By reducing fake profiles, encouraging transparency, and promoting professional accountability, we aim to build a network where trust is earned and maintained.",
  "At PRONET, a Pro ID is more than a badge. It represents a commitment to professionalism, integrity, and mutual respect. Every Pro ID member becomes part of a growing community dedicated to raising the standards of the photography and videography industry.",
  "PRONET is a professional network built around clear profiles, responsible conduct and trusted connections.",
];

export function Verification() {
  const { settings } = useSettings();
  const v = settings.verification_page || {};
  const paragraphs = v.paragraphs?.length ? v.paragraphs : VERIFICATION_PARAGRAPHS;
  useSeo({
    title: "Member Verification Process | PRONET",
    description: "How PRONET reviews photographers, videographers and studios before granting a PRONET Pro ID.",
  });
  return (
    <PageShell>
      <div className="overline text-fuchsia-300">Trust &amp; Safety</div>
      <h1 className="mt-3 font-display text-5xl md:text-6xl leading-none text-white" data-testid="verification-title">
        Verification <span className="italic text-gradient">Process</span>
      </h1>
      <p className="mt-4 text-white/60 max-w-2xl">
        Learn how professionals earn a PRONET Pro ID and what it confirms.
      </p>

      <div className="mt-10 glass-strong rounded-3xl p-6 md:p-10 max-w-3xl">
        <div className="flex items-center gap-3 pb-6 mb-6 border-b border-white/10">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300">
            <ShieldCheck size={22} />
          </span>
          <div>
            <div className="font-display text-xl text-white leading-tight">PRONET Pro ID</div>
            <div className="text-xs text-white/50">Digital Membership ID · Unique QR code · Reviewed by real humans</div>
          </div>
        </div>
        <LongCopy paragraphs={paragraphs} />
      </div>

      <div className="mt-10 flex items-center gap-3 text-xs">
        <Link to="/about" className="text-fuchsia-300 hover:underline">Back to About</Link>
        <span className="text-white/30">·</span>
        <Link to="/privacy" className="text-fuchsia-300 hover:underline">Privacy Policy</Link>
      </div>
    </PageShell>
  );
}

/* ---------- Contact (unchanged) ---------- */

export function Contact() {
  const { settings } = useSettings();
  const c = settings.contact_page || {};
  const [form, setForm] = useState({ full_name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useSeo({
    title: "Contact PRONET",
    description: "Contact the PRONET team for support, membership or partnership enquiries.",
  });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.full_name.trim()) return setErr("Please enter your full name");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return setErr("Please enter a valid email address");
    if (form.message.trim().length < 10) return setErr("Message should be at least 10 characters");
    setBusy(true);
    try {
      // Post to a public contact endpoint; graceful fallback if backend not yet wired
      const response = await fetch(`${BACKEND_URL}/api/public/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          message: form.message.trim(),
        }),
      });
      if (!response.ok) {
        throw new Error("Unable to send your message right now.");
      }
      setDone(true);
      trackLead("Contact Form");
      setForm({ full_name: "", email: "", message: "" });
    } catch (error) {
      setErr(error.message || "Unable to send your message right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative container-tight py-16 md:py-20 max-w-2xl">
      <div className="blobs-layer">
        <div className="blob blob-magenta w-[500px] h-[500px] -top-20 -right-20 opacity-35" />
      </div>
      <div className="relative z-10 fade-up">
        <div className="overline text-fuchsia-300">{c.overline || "Contact"}</div>
        <h1 className="mt-3 font-display text-5xl md:text-7xl leading-none text-white" data-testid="contact-title">
          {c.title_prefix || "Get in"} <span className="italic text-gradient">{c.title_accent || "touch."}</span>
        </h1>
        <p className="mt-6 text-white/60 max-w-xl" data-testid="contact-desc">
          {c.description || "Have a question or a partnership idea? Send us a note and we'll get back to you soon."}
        </p>

        {done ? (
          <div className="mt-10 glass rounded-3xl p-8 md:p-10 text-center" data-testid="contact-form-success">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300">
              <CheckIcon />
            </span>
            <div className="mt-4 font-display text-2xl text-white">Message sent</div>
            <p className="mt-2 text-sm text-white/60">Thanks for contacting us. We will reply by email shortly.</p>
            <button onClick={() => setDone(false)} data-testid="contact-form-reset"
              className="mt-6 btn-ghost rounded-full px-5 py-2 text-sm font-semibold text-white">
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="mt-10 glass rounded-3xl p-6 md:p-8 space-y-5" data-testid="contact-form">
            <ContactField label="Full name" required testid="contact-name"
              value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
            <ContactField label="Email address" required type="email" testid="contact-email-input"
              value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <div>
              <label className="overline text-white/60 text-[10px]">Message <span className="text-fuchsia-400">*</span></label>
              <textarea data-testid="contact-message" required rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="How can we help?"
                className="mt-1.5 w-full rounded-md bg-white/5 border border-white/10 text-white placeholder:text-white/35 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500/50" />
            </div>
            {err && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="contact-form-error">{err}</div>}
            <button type="submit" disabled={busy} data-testid="contact-form-submit"
              className="btn-glow inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-60">
              {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ContactField({ label, value, onChange, required, testid, type = "text" }) {
  return (
    <div>
      <label className="overline text-white/60 text-[10px]">{label} {required && <span className="text-fuchsia-400">*</span>}</label>
      <input
        data-testid={testid}
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full h-11 rounded-md bg-white/5 border border-white/10 text-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500/50"
      />
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function NotFound() {
  useSeo({ title: "Page Not Found | PRONET", noindex: true });
  return (
    <div className="container-tight py-32 text-center">
      <div className="font-display text-8xl text-gradient">404</div>
      <p className="mt-4 text-white/60">This page doesn't exist.</p>
      <Link to="/" className="mt-8 inline-block btn-glow rounded-full px-6 py-3 text-sm font-semibold text-white">Back to home</Link>
    </div>
  );
}
